<template>
    <div class="ding-trial-root">
        <MaintCollapsibleBanner
            title="钉钉测试推送"
            icon="el-icon-message"
            storage-key="dingTalkPushStyleTrial"
        >
            <div class="desc-item">
                <span class="desc-badge badge-primary">ActionCard</span>
                <span>固定 <code>action_card</code> + 语义色，用于验证能否收到推送、跳转是否正常。右侧为相似示意。</span>
            </div>
            <div class="desc-item">
                <span class="desc-badge badge-warning">注意</span>
                <span>必须填写接收人 userid；仅运维沙箱，与业务推送相互独立。</span>
            </div>
        </MaintCollapsibleBanner>

        <div class="ding-trial-toolbar">
            <div class="school-chip" :class="{ 'is-ready': schoolDepartmentId > 0 }">
                <i class="el-icon-office-building"></i>
                <template v-if="schoolDepartmentId > 0">
                    <span class="school-chip-label">学校部门</span>
                    <code>{{ schoolDepartmentId }}</code>
                </template>
                <span v-else class="school-chip-empty">请先在顶栏选择学校</span>
            </div>
            <div class="toolbar-actions">
                <el-button size="mini" :disabled="sending" @click="resetForm">重置</el-button>
                <el-button
                    type="primary"
                    size="mini"
                    icon="el-icon-s-promotion"
                    class="send-btn"
                    :loading="sending"
                    :disabled="!canSend"
                    @click="doSend"
                >发送测试推送</el-button>
            </div>
        </div>

        <div class="ding-trial-grid">
            <section class="panel config-panel">
                <header class="panel-head">
                    <span class="panel-kicker">01</span>
                    <h3>接收端</h3>
                </header>
                <div class="choice-grid choice-grid-2">
                    <button
                        v-for="a in audienceOptions"
                        :key="a.value"
                        type="button"
                        class="choice-card choice-card-sm"
                        :class="{ 'is-active': form.audience === a.value }"
                        @click="onAudienceChange(a.value)"
                    >
                        <i :class="a.icon"></i>
                        <strong>{{ a.label }}</strong>
                    </button>
                </div>

                <header class="panel-head panel-head-gap">
                    <span class="panel-kicker">02</span>
                    <h3>业务类型</h3>
                </header>
                <div class="choice-grid choice-grid-3">
                    <button
                        v-for="b in bizOptions"
                        :key="b.value"
                        type="button"
                        class="choice-card choice-card-sm"
                        :class="{ 'is-active': form.biz === b.value }"
                        @click="onBizChange(b.value)"
                    >
                        <strong>{{ b.label }}</strong>
                    </button>
                </div>

                <header class="panel-head panel-head-gap">
                    <span class="panel-kicker">03</span>
                    <h3>状态 / 场景</h3>
                </header>
                <div class="choice-grid choice-grid-3">
                    <button
                        v-for="s in statusOptions"
                        :key="s.value"
                        type="button"
                        class="choice-card choice-card-sm"
                        :class="{ 'is-active': form.status === s.value }"
                        @click="form.status = s.value"
                    >
                        <strong>{{ s.label }}</strong>
                        <span v-if="s.hint" class="choice-hint">{{ s.hint }}</span>
                    </button>
                </div>

                <header class="panel-head panel-head-gap">
                    <span class="panel-kicker">04</span>
                    <h3>接收与跳转</h3>
                </header>
                <div class="field-stack">
                    <label class="field-label">接收人 userid <em>*</em></label>
                    <el-input
                        v-model="form.receiverUserIds"
                        type="textarea"
                        :rows="2"
                        resize="none"
                        placeholder="钉钉 userid，多个用逗号分隔"
                    />
                    <label class="field-label">跳转 URL</label>
                    <el-input
                        v-model="form.messageUrl"
                        clearable
                        placeholder="可空；默认 https://h5.yiqiba.xin/"
                    />
                </div>

                <section v-if="lastResult" class="result-panel">
                    <header class="panel-head panel-head-gap">
                        <span class="panel-kicker">结果</span>
                        <h3>最近一次 API 返回</h3>
                        <el-tag
                            size="mini"
                            :type="lastResult.Success ? 'success' : 'danger'"
                            effect="dark"
                            class="result-tag"
                        >{{ lastResult.Success ? '成功' : '失败' }}</el-tag>
                    </header>
                    <div class="result-grid">
                        <div class="result-item"><span>学校</span><b>{{ lastResult.SchoolName || '-' }}</b></div>
                        <div class="result-item"><span>AgentId</span><b>{{ lastResult.AgentId || '-' }}</b></div>
                        <div class="result-item"><span>类型</span><b>actionCard</b></div>
                        <div class="result-item wide"><span>模板键</span><b>{{ lastResult.Template || '-' }}</b></div>
                        <div class="result-item wide"><span>接收人</span><b>{{ lastResult.ReceiverUserIds || '-' }}</b></div>
                        <div class="result-item wide"><span>标题</span><b>{{ lastResult.PreviewTitle || '-' }}</b></div>
                        <div class="result-item wide">
                            <span>正文</span>
                            <pre class="result-pre">{{ lastResult.PreviewContent || '-' }}</pre>
                        </div>
                        <div class="result-item"><span>errcode</span><b>{{ lastResult.Errcode }}</b></div>
                        <div class="result-item"><span>taskId</span><b>{{ lastResult.DingTaskId || '-' }}</b></div>
                        <div class="result-item wide"><span>errmsg</span><b>{{ lastResult.Errmsg || '-' }}</b></div>
                    </div>
                </section>
            </section>

            <aside class="panel preview-panel">
                <header class="panel-head">
                    <span class="panel-kicker">示意</span>
                    <h3>相似预览</h3>
                    <el-tag size="mini" type="info" effect="plain" class="approx-tag">非真机</el-tag>
                </header>
                <div class="dd-chat">
                    <div class="dd-chat-title">工作通知:一柒校园</div>
                    <div class="dd-msg">
                        <div class="dd-avatar" aria-hidden="true">壹</div>
                        <div class="dd-msg-main">
                            <div class="dd-sender">一柒吧</div>

                            <div class="dd-card">
                                <div class="dd-card-body">
                                    <div class="dd-md-head">{{ mockMarkdownHead }}</div>
                                    <div class="dd-form">
                                        <div v-for="(row, idx) in mockFormRows" :key="idx" class="dd-form-row">
                                            <span class="dd-form-k">{{ row.key }}：</span>
                                            <span class="dd-form-v" :class="row.accentClass">{{ row.value }}</span>
                                        </div>
                                    </div>
                                </div>
                                <div class="dd-action-bar">
                                    <span>查看详情</span>
                                    <i class="el-icon-arrow-right"></i>
                                </div>
                            </div>
                        </div>
                    </div>
                    <p class="preview-hint">仅相似示意，请点发送后到钉钉确认</p>
                </div>
            </aside>
        </div>
    </div>
</template>

<script>
import MaintCollapsibleBanner from '../components/MaintCollapsibleBanner';
import { dingTalkPushOpsSendTrial } from '../../../api/modules/ops-maintenance';

/** 与后端 DingTalkTestPushCatalog 字段对齐（不含「学校」）。创建于 2026-10-26，最后更新：2026-10-27 */
const STU = '张三（三年二班）';
const LEAVE_TIME = '今日 08:00~16:00';
const PUSH_CATALOG = [
    // 教师 · 请假
    { audience: 'teacher', biz: 'leave', status: 'pending', aLabel: '教师端', bLabel: '请假', sLabel: '待审批', hint: '橙', heading: '请假待审批', fields: [
        { key: '学生', value: STU }, { key: '类型', value: '事假' }, { key: '时间', value: LEAVE_TIME },
        { key: '状态', value: '待审批', accentClass: 'is-pending' }, { key: '事由', value: '家中有事', accentClass: 'is-pending' }
    ]},
    { audience: 'teacher', biz: 'leave', status: 'pass', aLabel: '教师端', bLabel: '请假', sLabel: '已通过抄送', hint: '绿', heading: '请假通知', fields: [
        { key: '学生', value: STU }, { key: '类型', value: '事假' }, { key: '结果', value: '已通过', accentClass: 'is-success' },
        { key: '时间', value: LEAVE_TIME }
    ]},
    { audience: 'teacher', biz: 'leave', status: 'reject', aLabel: '教师端', bLabel: '请假', sLabel: '已拒绝', hint: '红', heading: '请假通知', fields: [
        { key: '学生', value: STU }, { key: '类型', value: '事假' }, { key: '结果', value: '已拒绝', accentClass: 'is-reject' },
        { key: '时间', value: LEAVE_TIME }
    ]},
    { audience: 'teacher', biz: 'leave', status: 'revoke', aLabel: '教师端', bLabel: '请假', sLabel: '已撤销', hint: '蓝', heading: '请假通知', fields: [
        { key: '学生', value: STU }, { key: '类型', value: '事假' }, { key: '结果', value: '已撤销', accentClass: 'is-info' },
        { key: '时间', value: LEAVE_TIME }
    ]},
    { audience: 'teacher', biz: 'agentleave', status: 'pending', aLabel: '教师端', bLabel: '代请假', sLabel: '待审批', hint: '橙', heading: '代请假待审批', fields: [
        { key: '学生', value: STU }, { key: '类型', value: '事假' }, { key: '状态', value: '待审批', accentClass: 'is-pending' },
        { key: '时间', value: '今日至明日' }, { key: '事由', value: '家中有事', accentClass: 'is-pending' }
    ]},
    // 家长 · 请假 / 退餐
    { audience: 'parent', biz: 'leave', status: 'pass', aLabel: '家长端', bLabel: '请假', sLabel: '已通过', hint: '绿', heading: '请假通知', fields: [
        { key: '学生', value: STU }, { key: '结果', value: '已通过', accentClass: 'is-success' },
        { key: '类型', value: '事假' }, { key: '时间', value: LEAVE_TIME }
    ]},
    { audience: 'parent', biz: 'leave', status: 'reject', aLabel: '家长端', bLabel: '请假', sLabel: '已拒绝', hint: '红', heading: '请假通知', fields: [
        { key: '学生', value: STU }, { key: '结果', value: '已拒绝', accentClass: 'is-reject' },
        { key: '类型', value: '事假' }, { key: '时间', value: LEAVE_TIME },
        { key: '说明', value: '材料不全', accentClass: 'is-reject' }
    ]},
    { audience: 'parent', biz: 'mealrefund', status: 'pass', aLabel: '家长端', bLabel: '退餐', sLabel: '已同意', hint: '绿', heading: '退餐审核通知', fields: [
        { key: '学生', value: STU }, { key: '结果', value: '已同意', accentClass: 'is-success' },
        { key: '餐次', value: '今日午餐' }, { key: '订单号', value: 'A1234' }
    ]},
    { audience: 'parent', biz: 'mealrefund', status: 'reject', aLabel: '家长端', bLabel: '退餐', sLabel: '已拒绝', hint: '红', heading: '退餐审核通知', fields: [
        { key: '学生', value: STU }, { key: '结果', value: '已拒绝', accentClass: 'is-reject' },
        { key: '餐次', value: '今日午餐' }, { key: '订单号', value: 'A1234' },
        { key: '说明', value: '超出时限', accentClass: 'is-reject' }
    ]},
    // 考勤
    { audience: 'parent', biz: 'kaoqin', status: 'inout', aLabel: '家长端', bLabel: '进出校', sLabel: '进/出校', hint: '蓝', heading: '考勤通知', fields: [
        { key: '学生', value: STU }, { key: '状态', value: '进校', accentClass: 'is-info' }, { key: '时间', value: '今日 08:30' }
    ]},
    { audience: 'parent', biz: 'hostel', status: 'inout', aLabel: '家长端', bLabel: '宿舍考勤', sLabel: '进出寝室', hint: '蓝', heading: '宿舍通知', fields: [
        { key: '学生', value: STU }, { key: '状态', value: '进入寝室', accentClass: 'is-info' }, { key: '时间', value: '今日 21:10' }
    ]},
    { audience: 'parent', biz: 'rollcall', status: 'absent', aLabel: '家长端', bLabel: '班级点到', sLabel: '缺勤', hint: '橙', heading: '考勤通知', fields: [
        { key: '学生', value: STU }, { key: '状态', value: '缺勤', accentClass: 'is-pending' },
        { key: '班级', value: '三年二班' }, { key: '日期', value: '今日' }, { key: '时段', value: '上午' }
    ]},
    { audience: 'teacher', biz: 'kaoqin', status: 'self', aLabel: '教师端', bLabel: '教师考勤', sLabel: '打卡成功', hint: '蓝', heading: '教师考勤', fields: [
        { key: '教师', value: '李老师' }, { key: '状态', value: '上班打卡', accentClass: 'is-info' }, { key: '时间', value: '今日 07:55' }
    ]},
    { audience: 'teacher', biz: 'rollcall', status: 'urge', aLabel: '教师端', bLabel: '班级点到', sLabel: '催办', hint: '橙', heading: '班级点到', fields: [
        { key: '班级', value: '三年二班' }, { key: '日期', value: '今日' }, { key: '时段', value: '上午' },
        { key: '状态', value: '尚未完成点到', accentClass: 'is-pending' }
    ]},
    { audience: 'teacher', biz: 'absent', status: 'summary', aLabel: '教师端', bLabel: '课堂缺勤', sLabel: '缺勤汇总', hint: '橙', heading: '缺勤通知', fields: [
        { key: '班级', value: '三年二班' }, { key: '缺勤', value: '张三、李四', accentClass: 'is-pending' },
        { key: '类型', value: '缺勤' }, { key: '课程', value: '语文' }, { key: '节次', value: '第2节' }, { key: '时间', value: '08:30' }
    ]},
    { audience: 'teacher', biz: 'missing', status: 'attendance', aLabel: '教师端', bLabel: '未点名', sLabel: '课程催办', hint: '橙', heading: '课程点名提醒', fields: [
        { key: '班级', value: '三年二班' }, { key: '课程', value: '数学' }, { key: '节次', value: '第3节' },
        { key: '状态', value: '尚未点名', accentClass: 'is-pending' }
    ]},
    { audience: 'teacher', biz: 'daily', status: 'kaoqin', aLabel: '教师端', bLabel: '日汇总', sLabel: '考勤汇总', hint: '蓝', heading: '班主任考勤汇总', fields: [
        { key: '班级', value: '三年二班' }, { key: '日期', value: '今日' },
        { key: '应打卡', value: '42' }, { key: '准时', value: '38' }, { key: '超时', value: '2' }, { key: '未打卡', value: '2' }
    ]},
    { audience: 'teacher', biz: 'notreturn', status: 'remind', aLabel: '教师端', bLabel: '未返校', sLabel: '到期提醒', hint: '橙', heading: '未返校提醒', fields: [
        { key: '学生', value: STU }, { key: '班级', value: '三年二班' },
        { key: '状态', value: '请假到期未返校', accentClass: 'is-pending' }
    ]},
    // 放学
    { audience: 'parent', biz: 'dismissal', status: 'done', aLabel: '家长端', bLabel: '放学', sLabel: '已放学', hint: '绿', heading: '放学通知', fields: [
        { key: '班级', value: '三年二班' }, { key: '状态', value: '已放学', accentClass: 'is-success' }, { key: '时间', value: '16:30' }
    ]},
    { audience: 'parent', biz: 'dismissal', status: 'detain', aLabel: '家长端', bLabel: '放学', sLabel: '滞留', hint: '橙', heading: '滞留通知', fields: [
        { key: '学生', value: STU }, { key: '班级', value: '三年二班' },
        { key: '状态', value: '暂缓离校', accentClass: 'is-pending' }, { key: '时间', value: '16:30' },
        { key: '说明', value: '请家长及时联系班主任', accentClass: 'is-pending' }
    ]},
    { audience: 'teacher', biz: 'dismissal', status: 'notify', aLabel: '教师端', bLabel: '放学', sLabel: '班级提醒', hint: '蓝', heading: '放学通知', fields: [
        { key: '班级', value: '三年二班' }, { key: '时间', value: '16:30' },
        { key: '状态', value: '待组织放学', accentClass: 'is-info' }
    ]},
    // 德育
    { audience: 'parent', biz: 'moral', status: 'eval', aLabel: '家长端', bLabel: '德育评价', sLabel: '新评价', hint: '蓝', heading: '学生评价', fields: [
        { key: '学生', value: '02张三' }, { key: '评价项目', value: '课堂表现优秀', accentClass: 'is-info' },
        { key: '班级', value: '三年二班' }, { key: '时间', value: '10:20' }
    ]},
    { audience: 'parent', biz: 'moral', status: 'alert', aLabel: '家长端', bLabel: '德育警戒线', sLabel: '触及警戒', hint: '红', heading: '德育警戒线提醒', fields: [
        { key: '学生', value: STU }, { key: '维度', value: '纪律' },
        { key: '累计扣分', value: '本周-12', accentClass: 'is-reject' },
        { key: '状态', value: '触及警戒线', accentClass: 'is-reject' }
    ]},
    { audience: 'teacher', biz: 'classcheck', status: 'update', aLabel: '教师端', bLabel: '班级检查', sLabel: '分值变更', hint: '蓝', heading: '班级检查更新', fields: [
        { key: '班级', value: '三年二班' }, { key: '项目', value: '卫生检查' }, { key: '时间', value: '10:20' },
        { key: '状态', value: '分值已更新', accentClass: 'is-info' }
    ]},
    { audience: 'teacher', biz: 'eval', status: 'deduct', aLabel: '教师端', bLabel: '扣分提醒', sLabel: '学生扣分', hint: '橙', heading: '学生扣分提醒', fields: [
        { key: '学生', value: STU }, { key: '结果', value: '扣分', accentClass: 'is-pending' },
        { key: '项目', value: '纪律' }, { key: '分值', value: '-2' }
    ]},
    { audience: 'teacher', biz: 'eval', status: 'alert', aLabel: '教师端', bLabel: '警戒线', sLabel: '触及警戒', hint: '红', heading: '德育警戒线提醒', fields: [
        { key: '对象', value: '三年二班' }, { key: '维度', value: '纪律' },
        { key: '累计扣分', value: '-12', accentClass: 'is-reject' },
        { key: '状态', value: '触及警戒线', accentClass: 'is-reject' }
    ]},
    { audience: 'teacher', biz: 'honor', status: 'pending', aLabel: '教师端', bLabel: '荣誉采集', sLabel: '待审批', hint: '橙', heading: '荣誉采集待审批', fields: [
        { key: '学生', value: STU }, { key: '荣誉', value: '三好学生' },
        { key: '状态', value: '待审批', accentClass: 'is-pending' }
    ]},
    { audience: 'teacher', biz: 'redflag', status: 'review', aLabel: '教师端', bLabel: '流动红旗', sLabel: '复核', hint: '橙', heading: '流动红旗通知', fields: [
        { key: '班级', value: '三年二班' }, { key: '周次', value: '第12周' }, { key: '得分', value: '96' }, { key: '名次', value: '第2名' },
        { key: '状态', value: '待复核', accentClass: 'is-pending' }
    ]},
    { audience: 'teacher', biz: 'fiveedu', status: 'urge', aLabel: '教师端', bLabel: '五育', sLabel: '评价催办', hint: '橙', heading: '五育评价提醒', fields: [
        { key: '班级', value: '三年二班' }, { key: '轮次', value: '第2轮' }, { key: '科目', value: '体育' },
        { key: '状态', value: '待评价', accentClass: 'is-pending' }
    ]},
    // 调代课
    { audience: 'teacher', biz: 'courseadj', status: 'pending', aLabel: '教师端', bLabel: '调代课', sLabel: '待审批', hint: '橙', heading: '调代课申请提醒', fields: [
        { key: '申请人', value: '王老师' }, { key: '课程', value: '三年二班 · 语文' },
        { key: '节次', value: '第3-4节' }, { key: '时间', value: LEAVE_TIME },
        { key: '状态', value: '待审批', accentClass: 'is-pending' }, { key: '原因', value: '开会冲突' }
    ]},
    { audience: 'teacher', biz: 'courseadj', status: 'arranged', aLabel: '教师端', bLabel: '调代课', sLabel: '已安排', hint: '绿', heading: '调代课安排通知', fields: [
        { key: '课程', value: '三年二班 · 语文' }, { key: '节次', value: '第3-4节' },
        { key: '结果', value: '已通过生效', accentClass: 'is-success' }, { key: '时间', value: LEAVE_TIME }
    ]},
    { audience: 'teacher', biz: 'courseadj', status: 'reject', aLabel: '教师端', bLabel: '调代课', sLabel: '已驳回', hint: '红', heading: '调代课申请提醒', fields: [
        { key: '课程', value: '三年二班 · 语文' }, { key: '节次', value: '第3-4节' },
        { key: '结果', value: '已驳回', accentClass: 'is-reject' }, { key: '时间', value: LEAVE_TIME },
        { key: '说明', value: '时间冲突无法安排', accentClass: 'is-reject' }
    ]},
    // 班级圈
    { audience: 'teacher', biz: 'circle', status: 'pending', aLabel: '教师端', bLabel: '班级圈', sLabel: '待审核', hint: '橙', heading: '班级圈待审核', fields: [
        { key: '班级', value: '三年二班' }, { key: '发布人', value: '李老师' },
        { key: '状态', value: '待审核', accentClass: 'is-pending' }, { key: '摘要', value: '今日课堂精彩瞬间' }
    ]},
    { audience: 'parent', biz: 'circle', status: 'pass', aLabel: '家长端', bLabel: '班级圈', sLabel: '审核通过', hint: '绿', heading: '班级圈审核通过', fields: [
        { key: '班级', value: '三年二班' }, { key: '学生', value: '张三' },
        { key: '结果', value: '已通过', accentClass: 'is-success' }
    ]},
    { audience: 'parent', biz: 'circle', status: 'reject', aLabel: '家长端', bLabel: '班级圈', sLabel: '审核未通过', hint: '红', heading: '班级圈审核未通过', fields: [
        { key: '班级', value: '三年二班' }, { key: '学生', value: '张三' },
        { key: '结果', value: '未通过', accentClass: 'is-reject' },
        { key: '说明', value: '内容不符合规范', accentClass: 'is-reject' }
    ]},
    { audience: 'parent', biz: 'circle', status: 'post', aLabel: '家长端', bLabel: '班级圈', sLabel: '新动态', hint: '蓝', heading: '班级圈更新', fields: [
        { key: '班级', value: '三年二班' }, { key: '时间', value: '16:40' },
        { key: '状态', value: '已更新', accentClass: 'is-info' }
    ]},
    // 校车余额
    { audience: 'parent', biz: 'bus', status: 'board', aLabel: '家长端', bLabel: '校车', sLabel: '上车/下车', hint: '蓝', heading: '校车提醒', fields: [
        { key: '学生', value: STU }, { key: '状态', value: '已上车', accentClass: 'is-info' },
        { key: '车牌', value: '浙A·12345' }, { key: '时间', value: '16:40' }
    ]},
    { audience: 'parent', biz: 'balance', status: 'low', aLabel: '家长端', bLabel: '消费余额', sLabel: '余额不足', hint: '橙', heading: '余额提醒', fields: [
        { key: '学生', value: STU }, { key: '状态', value: '余额不足', accentClass: 'is-pending' },
        { key: '余额', value: '3.50 元', accentClass: 'is-pending' }
    ]},
    // 报修
    { audience: 'teacher', biz: 'repair', status: 'pending', aLabel: '教师端', bLabel: '报修', sLabel: '待处理', hint: '橙', heading: '报修通知', fields: [
        { key: '单号', value: 'BX20261026001' }, { key: '状态', value: '待审批', accentClass: 'is-pending' },
        { key: '位置', value: '教学楼301' }, { key: '说明', value: '灯管损坏' }
    ]},
    { audience: 'teacher', biz: 'repair', status: 'done', aLabel: '教师端', bLabel: '报修', sLabel: '已完成', hint: '绿', heading: '报修完成通知', fields: [
        { key: '单号', value: 'BX20261026001' }, { key: '结果', value: '已完成', accentClass: 'is-success' },
        { key: '位置', value: '教学楼301' }, { key: '说明', value: '灯管损坏' }
    ]},
    { audience: 'teacher', biz: 'repair', status: 'reject', aLabel: '教师端', bLabel: '报修', sLabel: '已驳回', hint: '红', heading: '报修通知', fields: [
        { key: '单号', value: 'BX20261026001' }, { key: '结果', value: '已驳回', accentClass: 'is-reject' },
        { key: '位置', value: '教学楼301' }, { key: '说明', value: '非本校报修范围', accentClass: 'is-reject' }
    ]},
    // 会议/车位/备课
    { audience: 'teacher', biz: 'meeting', status: 'booked', aLabel: '教师端', bLabel: '会议室', sLabel: '预约成功', hint: '绿', heading: '会议室预约通知', fields: [
        { key: '主题', value: '年级组例会' }, { key: '会议室', value: '一楼报告厅' },
        { key: '结果', value: '预约成功', accentClass: 'is-success' }, { key: '时间', value: LEAVE_TIME }
    ]},
    { audience: 'teacher', biz: 'meeting', status: 'remind', aLabel: '教师端', bLabel: '会议室', sLabel: '开始提醒', hint: '橙', heading: '会议开始提醒', fields: [
        { key: '主题', value: '年级组例会' }, { key: '会议室', value: '一楼报告厅' },
        { key: '状态', value: '即将开始', accentClass: 'is-pending' }, { key: '时间', value: LEAVE_TIME }
    ]},
    { audience: 'teacher', biz: 'meeting', status: 'cancel', aLabel: '教师端', bLabel: '会议室', sLabel: '已取消', hint: '红', heading: '会议室取消通知', fields: [
        { key: '主题', value: '年级组例会' }, { key: '会议室', value: '一楼报告厅' },
        { key: '结果', value: '已取消', accentClass: 'is-reject' }, { key: '时间', value: LEAVE_TIME }
    ]},
    { audience: 'teacher', biz: 'parking', status: 'pending', aLabel: '教师端', bLabel: '停车场', sLabel: '车牌待审', hint: '橙', heading: '家长车牌审批', fields: [
        { key: '申请人', value: '张三家长' }, { key: '车牌', value: '浙A·12345' },
        { key: '状态', value: '待审批', accentClass: 'is-pending' }
    ]},
    { audience: 'teacher', biz: 'parking', status: 'temp', aLabel: '教师端', bLabel: '临时车', sLabel: '待审批', hint: '橙', heading: '临时车审批', fields: [
        { key: '申请人', value: '张三家长' }, { key: '车牌', value: '浙A·12345' },
        { key: '时间', value: '今日 08:00 至 今日 18:00' },
        { key: '状态', value: '待审批', accentClass: 'is-pending' }
    ]},
    { audience: 'teacher', biz: 'prep', status: 'urge', aLabel: '教师端', bLabel: '集体备课', sLabel: '催办', hint: '橙', heading: '集体备课-催办', fields: [
        { key: '教研组', value: '语文组' }, { key: '教案', value: '《春》第一课时' },
        { key: '状态', value: '待评价/投票', accentClass: 'is-pending' }
    ]},
    { audience: 'teacher', biz: 'prep', status: 'pass', aLabel: '教师端', bLabel: '集体备课', sLabel: '已通过', hint: '绿', heading: '集体备课-投票已通过', fields: [
        { key: '教研组', value: '语文组' }, { key: '教案', value: '《春》第一课时' },
        { key: '结果', value: '已通过', accentClass: 'is-success' }
    ]},
    // 心理 / 协约币 / 活动
    { audience: 'parent', biz: 'psych', status: 'remind', aLabel: '家长端', bLabel: '心理档案', sLabel: '催填', hint: '橙', heading: '心理档案填写提醒', fields: [
        { key: '学生', value: STU }, { key: '状态', value: '待填写', accentClass: 'is-pending' }
    ]},
    { audience: 'teacher', biz: 'psych', status: 'pending', aLabel: '教师端', bLabel: '心理档案', sLabel: '待评估', hint: '橙', heading: '心理档案待评估跟进', fields: [
        { key: '学生', value: '张三' }, { key: '班级', value: '三年二班' },
        { key: '状态', value: '待评估', accentClass: 'is-pending' }
    ]},
    { audience: 'parent', biz: 'coin', status: 'grant', aLabel: '家长端', bLabel: '协约币', sLabel: '发放', hint: '绿', heading: '协约币发放', fields: [
        { key: '学生', value: STU }, { key: '类型', value: '表现奖励' },
        { key: '面值', value: '+10', accentClass: 'is-success' },
        { key: '说明', value: '课堂表现优秀' }, { key: '时间', value: '10:20' }
    ]},
    { audience: 'teacher', biz: 'activity', status: 'notify', aLabel: '教师端', bLabel: '活动', sLabel: '活动通知', hint: '蓝', heading: '活动通知', fields: [
        { key: '活动', value: '春游安全教育' }, { key: '时间', value: LEAVE_TIME },
        { key: '学生', value: '张三、李四 等12人' }
    ]}
];

/**
 * 运维台：钉钉测试推送（端 × 业务 × 状态）。创建于 2026-10-20
 * 最后更新：2026-10-26
 */
export default {
    name: 'DingTalkPushStyleTrial',
    components: { MaintCollapsibleBanner },
    data() {
        return {
            schoolDepartmentId: 0,
            sending: false,
            lastResult: null,
            form: this.createDefaultForm(),
            audienceOptions: [
                { value: 'teacher', label: '教师端', icon: 'el-icon-s-custom' },
                { value: 'parent', label: '家长端', icon: 'el-icon-user' }
            ],
            catalog: PUSH_CATALOG
        };
    },
    computed: {
        canSend() {
            return this.schoolDepartmentId > 0
                && !!(this.form.receiverUserIds && this.form.receiverUserIds.trim())
                && !!this.templateKey
                && !this.sending;
        },
        templateKey() {
            if (!this.form.audience || !this.form.biz || !this.form.status) return '';
            return `${this.form.audience}_${this.form.biz}_${this.form.status}`;
        },
        bizOptions() {
            const map = new Map();
            this.catalog.filter(c => c.audience === this.form.audience).forEach(c => {
                if (!map.has(c.biz)) map.set(c.biz, { value: c.biz, label: c.bLabel });
            });
            return Array.from(map.values());
        },
        statusOptions() {
            return this.catalog
                .filter(c => c.audience === this.form.audience && c.biz === this.form.biz)
                .map(c => ({ value: c.status, label: c.sLabel, hint: c.hint }));
        },
        currentItem() {
            return this.catalog.find(c =>
                c.audience === this.form.audience
                && c.biz === this.form.biz
                && c.status === this.form.status
            ) || null;
        },
        mockFormRows() {
            return (this.currentItem && this.currentItem.fields) || [{ key: '说明', value: '请选择端/业务/状态' }];
        },
        mockMarkdownHead() {
            const h = (this.currentItem && this.currentItem.heading) || '通知';
            return `【${h}】`;
        }
    },
    watch: {
        '$store.state.activeSchoolId': {
            handler(newVal) {
                this.schoolDepartmentId = newVal ? Number(newVal) : 0;
            },
            immediate: true
        }
    },
    methods: {
        createDefaultForm() {
            return {
                audience: 'teacher',
                biz: 'leave',
                status: 'pending',
                receiverUserIds: '0810685055907572',
                messageUrl: ''
            };
        },
        onAudienceChange(val) {
            this.form.audience = val;
            const firstBiz = this.bizOptions[0];
            this.form.biz = firstBiz ? firstBiz.value : '';
            this.$nextTick(() => {
                const firstStatus = this.statusOptions[0];
                this.form.status = firstStatus ? firstStatus.value : '';
            });
        },
        onBizChange(val) {
            this.form.biz = val;
            this.$nextTick(() => {
                const firstStatus = this.statusOptions[0];
                this.form.status = firstStatus ? firstStatus.value : '';
            });
        },
        resetForm() {
            this.form = this.createDefaultForm();
            this.lastResult = null;
        },
        doSend() {
            if (!this.canSend) {
                this.$message.warning('请选择学校、模板并填写接收人 userid');
                return;
            }
            this.$confirm(
                `将发送「${this.mockMarkdownHead}」ActionCard 到填写账号，用于测试收件。是否继续？`,
                '确认测试推送',
                { type: 'warning', confirmButtonText: '发送', cancelButtonText: '取消' }
            ).then(() => {
                this.sending = true;
                const body = {
                    SchoolDepartmentId: this.schoolDepartmentId,
                    Audience: this.form.audience,
                    Biz: this.form.biz,
                    Status: this.form.status,
                    Template: this.templateKey,
                    ReceiverUserIds: this.form.receiverUserIds.trim(),
                    MessageUrl: (this.form.messageUrl || '').trim()
                };
                dingTalkPushOpsSendTrial(body).then(res => {
                    const ok = res.data && res.data.success;
                    const payload = (res.data && res.data.response) || null;
                    if (payload) this.lastResult = payload;
                    if (ok) {
                        this.$message.success((res.data && res.data.msg) || '已提交，请打开钉钉工作通知查看');
                    } else {
                        this.$message.error((res.data && res.data.msg) || '试推失败');
                    }
                }).catch(err => {
                    this.$message.error((err && err.message) || '请求失败');
                }).finally(() => {
                    this.sending = false;
                });
            }).catch(() => {});
        }
    }
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Fira+Sans:wght@400;500;600;700&family=Fira+Code:wght@400;500;600&display=swap');

.ding-trial-root {
    --trial-primary: #0f766e;
    --trial-secondary: #14b8a6;
    --trial-cta: #0369a1;
    --trial-bg: #f0fdfa;
    --trial-text: #134e4a;
    --trial-muted: #5b7c78;
    --trial-line: #cce8e3;
    --trial-card: #ffffff;
    display: flex;
    flex-direction: column;
    height: 100%;
    min-height: 0;
    overflow: hidden !important;
    padding: 0 2px 8px;
    font-family: 'Fira Sans', 'PingFang SC', 'Microsoft YaHei', sans-serif;
    color: var(--trial-text);
}

.ding-trial-toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    flex-shrink: 0;
    margin: 4px 0 10px;
    padding: 8px 10px;
    border-radius: 10px;
    background: var(--trial-card);
    border: 1px solid var(--trial-line);
}

.school-chip {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
    font-size: 13px;
    color: var(--trial-muted);
}

.school-chip i { color: var(--trial-secondary); }
.school-chip.is-ready { color: var(--trial-text); }
.school-chip-label { color: var(--trial-muted); }

.school-chip code {
    font-family: 'Fira Code', monospace;
    font-size: 12px;
    padding: 2px 8px;
    border-radius: 6px;
    background: var(--trial-bg);
    border: 1px solid var(--trial-line);
}

.toolbar-actions {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
}

.send-btn {
    background: var(--trial-cta) !important;
    border-color: var(--trial-cta) !important;
}

.ding-trial-grid {
    display: grid;
    grid-template-columns: minmax(0, 1fr) 340px;
    flex: 1 1 auto;
    gap: 16px;
    align-items: stretch;
    width: 100%;
    min-height: 0;
    overflow: hidden;
}

.config-panel {
    min-width: 0;
    min-height: 0;
    overflow-x: hidden;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
}

.panel {
    background: var(--trial-card);
    border: 1px solid var(--trial-line);
    border-radius: 12px;
    padding: 12px 14px 14px;
    box-sizing: border-box;
}

.panel-head {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 10px;
}

.panel-head-gap { margin-top: 16px; }

.panel-kicker {
    font-family: 'Fira Code', monospace;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.04em;
    color: var(--trial-primary);
    background: var(--trial-bg);
    border: 1px solid var(--trial-line);
    border-radius: 999px;
    padding: 2px 8px;
}

.panel-head h3 {
    margin: 0;
    font-size: 14px;
    font-weight: 650;
    color: var(--trial-text);
}

.approx-tag { margin-left: auto; }

.choice-grid {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 8px;
}

.choice-grid-4 {
    grid-template-columns: repeat(2, minmax(0, 1fr));
}

.choice-card {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    text-align: left;
    padding: 10px;
    border-radius: 10px;
    border: 1px solid var(--trial-line);
    background: #fbfffe;
    color: var(--trial-text);
    cursor: pointer;
    transition: border-color 0.2s ease, background 0.2s ease, box-shadow 0.2s ease;
}

.choice-card-sm {
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 6px;
    min-height: 64px;
    text-align: center;
}

.choice-card i {
    color: var(--trial-secondary);
    font-size: 16px;
    margin-top: 1px;
}

.choice-text {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
}

.choice-text strong,
.choice-card-sm strong {
    font-size: 13px;
    font-weight: 650;
    line-height: 1.25;
}

.choice-text span {
    font-size: 11px;
    color: var(--trial-muted);
    line-height: 1.35;
}

.choice-card:hover {
    border-color: #99f6e4;
    background: var(--trial-bg);
}

.choice-card.is-active {
    border-color: var(--trial-primary);
    background: linear-gradient(180deg, #ecfdf8 0%, #f0fdfa 100%);
    box-shadow: 0 0 0 1px rgba(15, 118, 110, 0.18);
}

.choice-card.is-active i,
.choice-card.is-active strong {
    color: var(--trial-primary);
}

.field-stack {
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.field-label {
    margin: 4px 0 0;
    font-size: 12px;
    font-weight: 600;
    color: var(--trial-muted);
}

.field-label em { color: #dc2626; font-style: normal; }

.preview-panel {
    width: 340px;
    min-width: 340px;
    max-width: 340px;
    min-height: 0;
    overflow-x: hidden;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
}

.dd-chat {
    background: #f5f6f7;
    border-radius: 10px;
    border: 1px solid #e8e8e8;
    padding: 10px 10px 12px;
    min-height: 420px;
}

.dd-chat-title {
    text-align: center;
    font-size: 12px;
    font-weight: 600;
    color: #171a1d;
    margin-bottom: 12px;
}

.dd-msg {
    display: flex;
    align-items: flex-start;
    gap: 8px;
}

.dd-avatar {
    flex: 0 0 36px;
    width: 36px;
    height: 36px;
    border-radius: 8px;
    background: linear-gradient(135deg, #0089ff, #0e7490);
    color: #fff;
    font-size: 13px;
    font-weight: 700;
    display: flex;
    align-items: center;
    justify-content: center;
}

.dd-msg-main {
    flex: 1 1 auto;
    min-width: 0;
}

.dd-sender {
    font-size: 12px;
    color: #171a1d;
    margin: 0 0 6px 2px;
}

.dd-card {
    background: #fff;
    border-radius: 8px;
    border: 1px solid #ebebeb;
    overflow: hidden;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
}

.dd-card-body {
    padding: 12px 12px 10px;
}

.dd-oa-head {
    padding: 6px 12px;
    font-size: 12px;
    color: #fff;
    background: #bdbdbd;
}

.dd-app-name {
    font-size: 13px;
    color: #0089ff;
    margin-bottom: 6px;
    line-height: 1.3;
}

.dd-title {
    font-size: 16px;
    font-weight: 700;
    color: #171a1d;
    line-height: 1.35;
    margin-bottom: 6px;
}

.dd-md-head {
    font-size: 20px;
    font-weight: 700;
    color: #171a1d;
    line-height: 1.35;
    margin-bottom: 10px;
}

.dd-content {
    margin: 0 0 8px;
    font-size: 13px;
    color: #a2a3a5;
    line-height: 1.45;
}

.dd-form {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.dd-form-row {
    font-size: 13px;
    line-height: 1.5;
    color: #171a1d;
}

.dd-form-k { color: #a2a3a5; }
.dd-form-v { color: #171a1d; }
.dd-form-v.is-pending { color: #ff6a00; }
.dd-form-v.is-success { color: #00b853; }
.dd-form-v.is-reject { color: #f4333c; }
.dd-form-v.is-info { color: #0089ff; }

.choice-hint {
    display: block;
    margin-top: 2px;
    font-size: 11px;
    font-weight: 500;
    color: var(--trial-muted);
}

.choice-grid-2 {
    grid-template-columns: repeat(2, minmax(0, 1fr));
}

.choice-grid-3 {
    grid-template-columns: repeat(3, minmax(0, 1fr));
}

.dd-action-bar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 11px 12px;
    border-top: 1px solid #f0f0f0;
    font-size: 14px;
    color: #171a1d;
}

.dd-action-bar i {
    color: #c4c4c4;
    font-size: 12px;
}

.preview-hint {
    margin: 12px 0 0;
    font-size: 11px;
    color: #a2a3a5;
    text-align: center;
}

.result-panel {
    margin-top: 8px;
    padding-top: 4px;
    border-top: 1px dashed var(--trial-line);
}

.result-tag { margin-left: auto; }

.result-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 8px;
}

.result-item {
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding: 8px 10px;
    border-radius: 8px;
    background: var(--trial-bg);
    border: 1px solid var(--trial-line);
}

.result-item.wide { grid-column: 1 / -1; }
.result-item span { font-size: 11px; color: var(--trial-muted); }
.result-item b { font-size: 13px; font-weight: 600; word-break: break-all; }

.result-pre {
    margin: 0;
    white-space: pre-wrap;
    word-break: break-word;
    font-family: 'Fira Code', monospace;
    font-size: 12px;
    line-height: 1.5;
    color: var(--trial-text);
}

.desc-item {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    margin-bottom: 8px;
    font-size: 13px;
    color: #475569;
    line-height: 1.5;
}

.desc-badge {
    flex-shrink: 0;
    display: inline-block;
    padding: 1px 8px;
    border-radius: 999px;
    font-size: 12px;
    font-weight: 600;
    color: #fff;
}

.badge-primary { background: #0e7490; }
.badge-warning { background: #d97706; }

@media (max-width: 980px) {
    .ding-trial-grid {
        grid-template-columns: 1fr;
    }

    .preview-panel {
        width: 100%;
        min-width: 0;
        max-width: none;
        max-height: 48vh;
    }

    .choice-grid { grid-template-columns: 1fr; }
    .choice-grid-4 { grid-template-columns: repeat(2, minmax(0, 1fr)); }
}
</style>
